// src/hooks/useCookieConsent.ts
import { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  getConsent,
  setConsent,
  type ConsentStatus as SharedConsentStatus,
} from '@bookkeeper/shared/consent';
import { syncPreconsentReplay } from '@/lib/preconsent-replay';
import { posthogOptIn, posthogOptOut, posthogRestoreConsent } from '@/lib/posthog';

/** Fired on `window` after a banner choice is stored, for UI that must stay clear of the banner. */
export const CONSENT_CHANGED_EVENT = 'bookeper:consent-changed';

type ConsentStatus = 'pending' | 'accepted' | 'declined';

interface UseCookieConsentReturn {
  status: ConsentStatus;
  isLoading: boolean;
  accept: () => void;
  decline: () => void;
}

function toConsentStatus(
  value: SharedConsentStatus | null
): Exclude<ConsentStatus, 'pending'> | null {
  return value === 'accepted' || value === 'declined' ? value : null;
}

/**
 * Run `fn` after the browser has painted the current frame. The accept click
 * must hide the banner before PostHog's opt-in work (recorder start, snapshot)
 * occupies the main thread, or the visitor sees a banner that ignored the click
 * and taps again (2026-10-27 audit: "Akceptuj" logged as a dead click).
 */
function afterNextPaint(fn: () => void): void {
  if (typeof requestAnimationFrame !== 'function') {
    setTimeout(fn, 0);
    return;
  }
  requestAnimationFrame(() => setTimeout(fn, 0));
}

/**
 * Hook for managing cookie consent state
 * Uses actual HTTP cookies for cross-subdomain sharing
 */
export function useCookieConsent(): UseCookieConsentReturn {
  // Start with loading state to prevent SSR hydration mismatch
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<ConsentStatus>('pending');

  // Read cookie on client-side only (after hydration)
  useEffect(() => {
    const existing = toConsentStatus(getConsent()?.status ?? null);
    // Avoid synchronous setState inside effect body (react-hooks/set-state-in-effect).
    const schedule = (cb: () => void) => {
      if (typeof queueMicrotask === 'function') {
        queueMicrotask(cb);
      } else {
        Promise.resolve().then(cb);
      }
    };

    schedule(() => {
      if (existing) {
        setStatus(existing);
      }
      setIsLoading(false);
    });
  }, []);

  const accept = useCallback(() => {
    if (getConsent()?.status === 'accepted') {
      setStatus('accepted');
      if (status !== 'accepted') posthogRestoreConsent();
      return;
    }
    setConsent('accepted');
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
    // Commit the hidden banner synchronously, inside the click: no await, no
    // exit animation, nothing between the click and the banner leaving.
    flushSync(() => setStatus('accepted'));
    // The main instance takes over at the next paint; the pre-consent recorder stops now, so no
    // frame has two recorders (#9200 Astra r3).
    syncPreconsentReplay('accepted');
    // posthogOptIn records the consent decision while the SDK is still
    // cookieless (same session as the click), then switches to the cookie id.
    afterNextPaint(posthogOptIn);
  }, [status]);

  const decline = useCallback(() => {
    setConsent('declined');
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
    flushSync(() => setStatus('declined'));
    // posthogOptOut is deferred past the next paint, and the pre-consent recording kept emitting
    // until then: it stops inside the click (#9200 Astra r3).
    syncPreconsentReplay('declined');
    afterNextPaint(posthogOptOut);
  }, []);

  return { status, isLoading, accept, decline };
}
